const API_BASE =
  import.meta.env.VITE_API_BASE ||
  import.meta.env.VITE_API_URL ||
  "http://127.0.0.1:8000";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

async function parseJSONSafe(resp) {
  try {
    return await resp.json();
  } catch {
    return null;
  }
}

export async function registerPushServiceWorker() {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service worker is not supported in this browser.");
  }

  return navigator.serviceWorker.register("/sw.js");
}

export async function getExistingPushSubscription() {
  if (!("serviceWorker" in navigator)) return null;

  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

export async function subscribeToPush({
  branch = null,
  userEmail = null,
  deviceLabel = null,
  targetRole = null,
  isAdmin = false,
} = {}) {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service worker is not supported.");
  }

  if (!("PushManager" in window)) {
    throw new Error("Push notifications are not supported on this device.");
  }

  if (!("Notification" in window)) {
    throw new Error("Notifications are not supported in this browser.");
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission was denied.");
  }

  const reg = await registerPushServiceWorker();

  const keyResp = await fetch(`${API_BASE}/push/public-key`, {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "include",
  });

  const keyData = await parseJSONSafe(keyResp);

  if (!keyResp.ok || !keyData?.publicKey) {
    throw new Error(
      keyData?.detail || keyData?.message || "Failed to load push public key."
    );
  }

  let subscription = await reg.pushManager.getSubscription();

  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(keyData.publicKey),
    });
  }

  const json = subscription.toJSON();

  const payload = {
    endpoint: json?.endpoint || "",
    keys: {
      p256dh: json?.keys?.p256dh || "",
      auth: json?.keys?.auth || "",
    },
    branch: branch || null,
    user_email: userEmail || null,
    target_role: targetRole || null,
    device_label: deviceLabel || navigator.userAgent || "browser",
    is_admin: !!isAdmin,
  };

  if (!payload.endpoint || !payload.keys.p256dh || !payload.keys.auth) {
    throw new Error("Browser returned an invalid push subscription.");
  }

  const saveResp = await fetch(`${API_BASE}/push/subscribe`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  const saveData = await parseJSONSafe(saveResp);

  if (!saveResp.ok) {
    throw new Error(
      saveData?.detail || saveData?.message || "Failed to save push subscription."
    );
  }

  return saveData || { ok: true };
}

export async function unsubscribeFromPush() {
  if (!("serviceWorker" in navigator)) {
    return { ok: true, unsubscribed: false };
  }

  const reg = await navigator.serviceWorker.ready;
  const subscription = await reg.pushManager.getSubscription();

  if (!subscription) {
    return { ok: true, unsubscribed: false };
  }

  const json = subscription.toJSON();

  const payload = {
    endpoint: json?.endpoint || "",
  };

  try {
    await fetch(`${API_BASE}/push/unsubscribe`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify(payload),
    });
  } catch (_) {}

  await subscription.unsubscribe();
  return { ok: true, unsubscribed: true };
}